import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import { FiberSitePlanningHeader } from './fiber_site_planning_header.component';
import FiberSitePlanningTable from './fiber_site_planning_table.component';
import FiberSitePlanningOverview from './fiber_site_planning_overview.component';
import { AddInfoFiberSitePlanningForm } from './add_info_fiber_main.component';
import { RanDBUploadForm } from '../RanDB/ran_db_upload_form_header.component';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  allSitesFiberSitePlanning,
  openAddInfoFormFiberSitePlanning,
  fiberSitePlanningViewMode,
  selectedFiberSitePlanningRow,
  openUploadFormRanDb,
} from '../../common/atom/atoms';
// Removed backdrop import - now using table-level loader
import { EditSingleSiteFiberSitePlanning } from './EditSingleSiteFiberSitePlanning';
import { FiberDashboardProvider, useFiberDashboard } from './context/fiberDashboardContext';
import { searchFiberSites } from './functions/search_fiber_sites.function';
// Reuse RAN DB upload modals for consistency
import UploadRanDBFileModal from '../RanDB/ran_db_upload_file_modal.component';
import RanDbUploadFileReview from '../RanDB/ran_db_upload_file_review_modal.component';
import DeleteSiteModal from '../RanDB/delete_site_modal_ran_db.component';

// Inner component that uses the dashboard context
const FiberSitePlanningContent = () => {
  const showAddInfoUploadForm = useAtomValue(openAddInfoFormFiberSitePlanning);
  const showUploadForm = useAtomValue(openUploadFormRanDb);
  const viewMode = useAtomValue(fiberSitePlanningViewMode);
  // Removed backdrop usage - now using table-level loader
  const { fetchDashboardData, dashboardData, refreshDashboardData, loading } = useFiberDashboard();

  const allFiberSitePlanningSite = useAtomValue(allSitesFiberSitePlanning);
  const setAllFiberSitePlanningSite = useSetAtom(allSitesFiberSitePlanning);
  const selectedRow = useAtomValue(selectedFiberSitePlanningRow);

  // Pagination and filtering state
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(50);
  const [totalCount, setTotalCount] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [selectedStatus, setSelectedStatus] = React.useState('All');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [showPagination, setShowPagination] = React.useState(true);
  const [sortBy, setSortBy] = React.useState('');
  const [sortOrder, setSortOrder] = React.useState('ASC');
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [searchResults, setSearchResults] = React.useState(null);

  const isTableLoading = loading;
  const isSearching = searchResults !== null;

  React.useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const effectiveSource = isSearching ? searchResults : dashboardData;

  const baseData = React.useMemo(
    () => (Array.isArray(effectiveSource) ? effectiveSource : []),
    [effectiveSource]
  );

  const columnFilteredData = React.useMemo(() => {
    if (!Array.isArray(columnFilters) || columnFilters.length === 0) {
      return baseData;
    }

    return baseData.filter((item) =>
      columnFilters.every(({ id, value }) => {
        if (value === undefined || value === null || value === '') {
          return true;
        }

        const itemValue = item?.[id];
        if (itemValue === undefined || itemValue === null) {
          return false;
        }

        if (Array.isArray(value)) {
          if (value.length === 0) {
            return true;
          }
          return value.some((val) =>
            String(itemValue).toLowerCase().includes(String(val).toLowerCase())
          );
        }

        return String(itemValue).toLowerCase().includes(String(value).toLowerCase());
      })
    );
  }, [baseData, columnFilters]);

  const statusFilteredData = React.useMemo(() => {
    if (selectedStatus === 'All') {
      return columnFilteredData;
    }
    const normalizedStatus = selectedStatus.toLowerCase();
    return columnFilteredData.filter((item) => {
      const value = item?.status;
      return value ? String(value).toLowerCase() === normalizedStatus : false;
    });
  }, [columnFilteredData, selectedStatus]);

  const sortedData = React.useMemo(() => {
    if (!sortBy) {
      return statusFilteredData;
    }

    const sorted = [...statusFilteredData].sort((a, b) => {
      const aValue = a?.[sortBy];
      const bValue = b?.[sortBy];

      if (aValue === bValue) {
        return 0;
      }
      if (aValue === undefined || aValue === null) {
        return -1;
      }
      if (bValue === undefined || bValue === null) {
        return 1;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return aValue - bValue;
      }

      return String(aValue).localeCompare(String(bValue), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });

    return sortOrder === 'DESC' ? sorted.reverse() : sorted;
  }, [statusFilteredData, sortBy, sortOrder]);

  const disablePagination = isSearching || selectedStatus !== 'All';

  React.useEffect(() => {
    const count = sortedData.length;
    if (totalCount !== count) {
      setTotalCount(count);
    }

    if (count === 0) {
      if (totalPages !== 1) {
        setTotalPages(1);
      }
      setAllFiberSitePlanningSite([]);
      setShowPagination(!disablePagination);
      if (!disablePagination && currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    if (disablePagination) {
      setAllFiberSitePlanningSite(sortedData);
      setShowPagination(false);
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      if (totalPages !== 1) {
        setTotalPages(1);
      }
      return;
    }

    const computedPages = Math.max(1, Math.ceil(count / pageSize));
    if (totalPages !== computedPages) {
      setTotalPages(computedPages);
    }

    setShowPagination(true);
    const safePage = Math.min(currentPage, computedPages);
    if (safePage !== currentPage) {
      setCurrentPage(safePage);
      return;
    }

    const startIndex = (safePage - 1) * pageSize;
    const pageSlice = sortedData.slice(startIndex, startIndex + pageSize);
    setAllFiberSitePlanningSite(pageSlice);
  }, [
    sortedData,
    isSearching,
    pageSize,
    currentPage,
    totalCount,
    totalPages,
    setAllFiberSitePlanningSite,
    disablePagination,
  ]);

  React.useEffect(() => {
    setShowPagination(!disablePagination);
  }, [disablePagination]);

  const performSearch = React.useCallback(async (value) => {
    const trimmed = (value || '').trim();
    if (trimmed.length <= 1) {
      setSearchResults(null);
      return;
    }

    try {
      const response = await searchFiberSites(trimmed);
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setSearchResults(data);
    } catch (error) {
      console.error('Error searching FTTS Site List sites:', error);
      setSearchResults([]);
    }
  }, []);

  const handleSearch = React.useCallback(
    (siteCode) => {
      setSearchQuery(siteCode || '');
      setCurrentPage(1);
      performSearch(siteCode);
    },
    [performSearch]
  );

  const handleClearSearch = React.useCallback(() => {
    setSearchQuery('');
    setSearchResults(null);
    setCurrentPage(1);
  }, []);

  const handleTableDataRefresh = React.useCallback(async () => {
    try {
      await refreshDashboardData();
      if (searchResults !== null) {
        await performSearch(searchQuery);
      }
    } catch (error) {
      console.error('Error refreshing fiber site planning data:', error);
    }
  }, [refreshDashboardData, searchResults, performSearch, searchQuery]);

  // Callback for bulk upload completion
  const handleBulkUploadComplete = React.useCallback(async () => {
    try {
      await refreshDashboardData();
      if (searchResults !== null) {
        await performSearch(searchQuery);
      }
      console.log('Fiber dashboard data refreshed after bulk upload');
    } catch (error) {
      console.error('Error refreshing fiber data after bulk upload:', error);
    }
  }, [refreshDashboardData, searchResults, performSearch, searchQuery]);

  useEffect(() => {
    if (viewMode === 'detailed') {
      fetchDashboardData();
    }

    const handleRefreshTableData = () => {
      handleTableDataRefresh();
    };

    window.addEventListener('refreshFiberTableData', handleRefreshTableData);

    return () => {
      window.removeEventListener('refreshFiberTableData', handleRefreshTableData);
    };
  }, [viewMode, fetchDashboardData, handleTableDataRefresh]);

  const handleSort = (field, direction) => {
    setSortBy(field);
    setSortOrder(direction);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    const parsedSize = Number(newPageSize);
    if (Number.isFinite(parsedSize) && parsedSize > 0) {
      setPageSize(parsedSize);
    } else {
      setPageSize(50);
    }
    setCurrentPage(1);
  };

  const handleStatusChange = (newStatus) => {
    setSelectedStatus(newStatus);
    setCurrentPage(1);
  };

  const handleColumnFiltersChange = React.useCallback((filters) => {
    setColumnFilters(filters || []);
    setCurrentPage(1);
  }, []);

  const renderContent = () => {
    if (showAddInfoUploadForm) {
      return <AddInfoFiberSitePlanningForm onSiteAdded={handleTableDataRefresh} />;
    } else if (showUploadForm) {
      return <RanDBUploadForm />;
    } else if (selectedRow) {
      return <EditSingleSiteFiberSitePlanning />;
    } else {
      return (
        <>
          <FiberSitePlanningHeader />
          {viewMode === 'overview' ? (
            <FiberSitePlanningOverview />
          ) : (
            <FiberSitePlanningTable
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              isLoading={isTableLoading}
              selectedStatus={selectedStatus}
              onStatusChange={handleStatusChange}
              showPagination={showPagination}
              searchQuery={searchQuery}
              onSearch={handleSearch}
              onClearSearch={handleClearSearch}
              isSearching={isSearching}
              onSort={handleSort}
              sortBy={sortBy}
              sortOrder={sortOrder}
              columnFilters={columnFilters}
              onColumnFiltersChange={handleColumnFiltersChange}
            />
          )}
        </>
      );
    }
  };

  return (
    <>
      <DashboardLayout>
        {renderContent()}
        {/* Upload flow modals reused from RAN DB */}
        <UploadRanDBFileModal />
        <RanDbUploadFileReview onUploadComplete={handleBulkUploadComplete} />
        <DeleteSiteModal />
      </DashboardLayout>
    </>
  );
};

// Main component that provides the dashboard context
export const FiberSitePlanningMain = () => {
  return (
    <FiberDashboardProvider>
      <FiberSitePlanningContent />
    </FiberDashboardProvider>
  );
};
